/**
 * SEO URL manifest for letspickquick.com.
 *
 * Every route listed here has a canonical slug (kebab-case, lowercase, no
 * trailing slash) and, until real content ships, is rendered by
 * <SeoStubPage /> with `robots: noindex, nofollow`. The stub deliberately
 * carries no thin content that could be indexed.
 *
 * `title`       Under 60 chars, follows "Page title | PickQuick" pattern.
 * `description` 150 to 160 chars, includes the primary keyword.
 *
 * As each page ships real copy, replace `component: "stub"` with a dedicated
 * component AND remove the `noindex` in the concrete page file. Only then
 * add the URL to /sitemap.xml.
 */

export const PLATFORM_PAGES = [
  {
    slug: "blinkit-seller-onboarding",
    label: "Blinkit seller onboarding",
    platform: "Blinkit",
    title: "Blinkit Seller Onboarding | PickQuick",
    description:
      "Blinkit seller onboarding for FMCG brands, done by PickQuick. Documentation, catalog compliance, dark store allocation and go-live in 14 to 21 working days.",
  },
  {
    slug: "zepto-seller-onboarding",
    label: "Zepto seller onboarding",
    platform: "Zepto",
    title: "Zepto Seller Onboarding | PickQuick",
    description:
      "Zepto seller onboarding for FMCG brands, done by PickQuick. GST, FSSAI, listings, catalog, purchase orders and dark store depth across 52 Indian cities.",
  },
  {
    slug: "swiggy-instamart-seller-onboarding",
    label: "Swiggy Instamart seller onboarding",
    platform: "Swiggy Instamart",
    title: "Swiggy Instamart Seller Onboarding | PickQuick",
    description:
      "Swiggy Instamart seller onboarding for FMCG brands. PickQuick handles paperwork, catalog, listings, dark store allocation and category access in India, end to end.",
  },
  {
    slug: "bigbasket-seller-onboarding",
    label: "BigBasket seller onboarding",
    platform: "BigBasket",
    title: "BigBasket Seller Onboarding | PickQuick",
    description:
      "BigBasket seller onboarding by PickQuick. From vendor code to city-level inventory, we onboard your brand on BigBasket end to end and run growth after go-live.",
  },
  {
    slug: "jiomart-seller-onboarding",
    label: "JioMart seller onboarding",
    platform: "JioMart",
    title: "JioMart Seller Onboarding | PickQuick",
    description:
      "JioMart seller onboarding by PickQuick. Full vendor setup, catalog compliance, dark store inventory and post-launch growth for FMCG brands across India.",
  },
];

export const CATEGORY_PAGES = [
  {
    slug: "quick-commerce-for-dairy-brands",
    label: "Quick commerce for dairy brands",
    title: "Quick Commerce for Dairy Brands | PickQuick",
    description:
      "Quick commerce for dairy brands on Blinkit, Zepto and Instamart. PickQuick already runs Gokul, Nandini and Aavin. We scale dairy from onboarding to growth.",
  },
  {
    slug: "quick-commerce-for-masala-brands",
    label: "Quick commerce for masala brands",
    title: "Quick Commerce for Masala Brands | PickQuick",
    description:
      "Quick commerce for masala brands, from Goldiee to Ramdev. PickQuick runs onboarding, keywords, price and dark store inventory across Blinkit, Zepto, Instamart.",
  },
  {
    slug: "quick-commerce-for-home-brands",
    label: "Quick commerce for home brands",
    title: "Quick Commerce for Home Brands | PickQuick",
    description:
      "Quick commerce for home and furnishing brands like D'decor. PickQuick handles catalog, price, pack strategy and growth on Blinkit, Zepto, Instamart, JioMart.",
  },
];

export const GUIDE_PAGES = [
  {
    slug: "guides/blinkit-vs-zepto",
    label: "Blinkit vs Zepto",
    title: "Blinkit vs Zepto for FMCG Sellers | PickQuick",
    description:
      "Blinkit vs Zepto for FMCG brands: onboarding, category rules, commissions, dark store depth and where each platform wins, from PickQuick's operators.",
  },
  {
    slug: "guides/how-to-sell-on-blinkit",
    label: "How to sell on Blinkit",
    title: "How to Sell on Blinkit as a Brand | PickQuick",
    description:
      "How to sell on Blinkit as an FMCG brand: paperwork, listings, purchase orders, dark store depth and growth playbooks, from PickQuick's Blinkit operators.",
  },
  {
    slug: "guides/how-to-sell-on-zepto",
    label: "How to sell on Zepto",
    title: "How to Sell on Zepto as a Brand | PickQuick",
    description:
      "How to sell on Zepto as an FMCG brand: GST, FSSAI, listings, dark store inventory, keywords and growth playbooks, from PickQuick's Zepto operators.",
  },
  {
    slug: "guides/blinkit-commission-structure",
    label: "Blinkit commission structure",
    title: "Blinkit Commission Structure Explained | PickQuick",
    description:
      "Blinkit commission structure explained: category slabs, fulfilment fees, ad take rates and how PickQuick negotiates the effective take-rate for FMCG brands.",
  },
];

/**
 * For each platform page, the two guide pages we cross-link to.
 * Descriptive anchor text is stored on the guide entry itself.
 */
export const PLATFORM_TO_GUIDES = {
  "blinkit-seller-onboarding": ["guides/how-to-sell-on-blinkit", "guides/blinkit-commission-structure"],
  "zepto-seller-onboarding":   ["guides/how-to-sell-on-zepto",   "guides/blinkit-vs-zepto"],
  "swiggy-instamart-seller-onboarding": ["guides/how-to-sell-on-blinkit", "guides/blinkit-vs-zepto"],
  "bigbasket-seller-onboarding":        ["guides/how-to-sell-on-blinkit", "guides/blinkit-commission-structure"],
  "jiomart-seller-onboarding":          ["guides/how-to-sell-on-blinkit", "guides/blinkit-vs-zepto"],
};

/**
 * For each guide page, the related platform page(s).
 */
export const GUIDE_TO_PLATFORMS = {
  "guides/blinkit-vs-zepto":             ["blinkit-seller-onboarding", "zepto-seller-onboarding"],
  "guides/how-to-sell-on-blinkit":       ["blinkit-seller-onboarding"],
  "guides/how-to-sell-on-zepto":         ["zepto-seller-onboarding"],
  "guides/blinkit-commission-structure": ["blinkit-seller-onboarding"],
};

export const findPageBySlug = (slug) =>
  [...PLATFORM_PAGES, ...CATEGORY_PAGES, ...GUIDE_PAGES].find((p) => p.slug === slug) || null;
